import {
    computed,
    readonly,
    ref,
    toValue,
    type MaybeRefOrGetter,
    type Ref,
} from 'vue';
import type { Options, SeriesOptionsType } from 'highcharts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';

const STACKABLE_SERIES_TYPES = new Set<string>(['column', 'bar', 'area', 'areaspline']);

function getSeriesRecord (series: SeriesOptionsType): Record<string, unknown> {
    return series as unknown as Record<string, unknown>;
}

function getSeriesTypeForExtents (series: Record<string, unknown>, chartType?: string): string {
    const t = series.type;
    if (typeof t === 'string') return t;
    if (typeof chartType === 'string' && chartType.length > 0) return chartType;
    return 'line';
}

function isAreaRangeType (type: string): boolean {
    return type === 'arearange' || type === 'arearangestep';
}

function getEffectiveStacking (
    series: Record<string, unknown>,
    plotOptions: Options['plotOptions'] | undefined,
    chartType?: string,
): string | undefined {
    const seriesStacking = series.stacking;
    if (seriesStacking === false) return undefined;
    if (typeof seriesStacking === 'string' && seriesStacking.length > 0 && seriesStacking !== 'undefined') {
        return seriesStacking;
    }

    const seriesType = getSeriesTypeForExtents(series, chartType);
    const plotOptsRecord = plotOptions as Record<string, { stacking?: string | boolean } | undefined> | undefined;
    const typeOpts = plotOptsRecord?.[seriesType];
    const typeStacking = typeOpts?.stacking;
    if (typeStacking === false) return undefined;
    if (typeof typeStacking === 'string' && typeStacking.length > 0 && typeStacking !== 'undefined') {
        return typeStacking;
    }

    const globalSeriesOpts = plotOptions?.series as { stacking?: string | boolean } | undefined;
    const globalStacking = globalSeriesOpts?.stacking;
    if (globalStacking === false) return undefined;
    if (typeof globalStacking === 'string' && globalStacking.length > 0 && globalStacking !== 'undefined') {
        return globalStacking;
    }

    return undefined;
}

function shouldUseStackedExtents (seriesType: string, effectiveStacking: string | undefined): boolean {
    if (effectiveStacking !== 'normal') return false;
    if (!STACKABLE_SERIES_TYPES.has(seriesType)) return false;
    if (isAreaRangeType(seriesType)) return false;
    return true;
}

function extractXyForStacking (point: unknown, index: number): { x: number; y: number } | null {
    if (point == null) return null;

    if (typeof point === 'number') {
        if (Number.isNaN(point)) return null;
        return { x: index, y: point };
    }

    if (Array.isArray(point) && point.length >= 2) {
        const rawX = point[0];
        const yVal = point[1];
        if (typeof yVal !== 'number' || Number.isNaN(yVal)) return null;
        const xNum = typeof rawX === 'number' ? rawX : Number(rawX);
        const x = Number.isFinite(xNum) ? xNum : index;
        return { x, y: yVal };
    }

    if (typeof point === 'object') {
        const po = point as Record<string, unknown>;
        if ('low' in po && 'high' in po) return null;
        const yVal = po.y;
        if (typeof yVal !== 'number' || Number.isNaN(yVal)) return null;
        const rawX = po.x;
        let xNum = Number.NaN;
        if (typeof rawX === 'number') {
            xNum = rawX;
        } else if (typeof rawX === 'string') {
            xNum = Number(rawX);
        }
        const x = Number.isFinite(xNum) ? xNum : index;
        return { x, y: yVal };
    }

    return null;
}

function collectYValuesFromPoint (point: unknown, yValues: number[]): void {
    if (point == null) return;

    if (typeof point === 'number') {
        if (!Number.isNaN(point)) yValues.push(point);
        return;
    }

    if (Array.isArray(point) && point.length >= 2) {
        const yValue = point[1];
        if (typeof yValue === 'number' && !Number.isNaN(yValue)) {
            yValues.push(yValue);
        }
        return;
    }

    if (typeof point === 'object') {
        const po = point as Record<string, unknown>;
        if ('y' in po && typeof po.y === 'number' && !Number.isNaN(po.y)) {
            yValues.push(po.y);
            return;
        }
        if ('low' in po && 'high' in po) {
            const low = po.low;
            const high = po.high;
            if (typeof low === 'number' && !Number.isNaN(low)) yValues.push(low);
            if (typeof high === 'number' && !Number.isNaN(high)) yValues.push(high);
        }
    }
}

function mergeStackedSeriesTotals (seriesList: ReadonlyArray<Record<string, unknown>>): { min: number; max: number } {
    const xTotals = new Map<number, { posSum: number; negSum: number }>();

    seriesList.forEach((series) => {
        const data = series.data;
        if (!data || !Array.isArray(data)) return;
        data.forEach((point, index) => {
            const xy = extractXyForStacking(point, index);
            if (xy == null) return;
            const entry = xTotals.get(xy.x) ?? { posSum: 0, negSum: 0 };
            if (xy.y > 0) entry.posSum += xy.y;
            else if (xy.y < 0) entry.negSum += xy.y;
            xTotals.set(xy.x, entry);
        });
    });

    if (xTotals.size === 0) {
        return { min: 0, max: 0 };
    }

    let stackMin = 0;
    let stackMax = 0;
    xTotals.forEach(({ posSum, negSum }) => {
        stackMin = Math.min(stackMin, negSum);
        stackMax = Math.max(stackMax, posSum);
    });

    return { min: stackMin, max: stackMax };
}

export function computeSharedYAxisExtents (
    plotOptions: Options['plotOptions'] | undefined,
    series: ReadonlyArray<SeriesOptionsType> | undefined,
    chartType?: string,
): { min: number; max: number } | null {
    if (series == null || series.length === 0) return null;

    const stackedByYAxis = new Map<number, Map<string, Record<string, unknown>[]>>();
    const nonStackedYValues: number[] = [];

    series.forEach((ser) => {
        const rec = getSeriesRecord(ser);
        const seriesType = getSeriesTypeForExtents(rec, chartType);
        const effectiveStacking = getEffectiveStacking(rec, plotOptions, chartType);

        if (shouldUseStackedExtents(seriesType, effectiveStacking)) {
            const yAxisIndex = typeof rec.yAxis === 'number' ? rec.yAxis : 0;
            const stackKey = rec.stack != null ? String(rec.stack) : '1';
            if (!stackedByYAxis.has(yAxisIndex)) {
                stackedByYAxis.set(yAxisIndex, new Map());
            }
            const stacksOnAxis = stackedByYAxis.get(yAxisIndex)!;
            const bucket = stacksOnAxis.get(stackKey) ?? [];
            bucket.push(rec);
            stacksOnAxis.set(stackKey, bucket);
        } else {
            const data = rec.data;
            if (!data || !Array.isArray(data)) return;
            data.forEach((point) => {
                collectYValuesFromPoint(point, nonStackedYValues);
            });
        }
    });

    let chartMin = Number.POSITIVE_INFINITY;
    let chartMax = Number.NEGATIVE_INFINITY;

    stackedByYAxis.forEach((stacksMap) => {
        let axisMin = Number.POSITIVE_INFINITY;
        let axisMax = Number.NEGATIVE_INFINITY;
        stacksMap.forEach((list) => {
            const { min: sMin, max: sMax } = mergeStackedSeriesTotals(list);
            axisMin = Math.min(axisMin, sMin);
            axisMax = Math.max(axisMax, sMax);
        });
        if (axisMin !== Number.POSITIVE_INFINITY) {
            chartMin = Math.min(chartMin, axisMin);
            chartMax = Math.max(chartMax, axisMax);
        }
    });

    if (nonStackedYValues.length > 0) {
        const nsMin = Math.min(...nonStackedYValues);
        const nsMax = Math.max(...nonStackedYValues);
        chartMin = Math.min(chartMin, nsMin);
        chartMax = Math.max(chartMax, nsMax);
    }

    if (chartMin === Number.POSITIVE_INFINITY || chartMax === Number.NEGATIVE_INFINITY) {
        return null;
    }

    const span = chartMax - chartMin;
    const padding = span === 0 ? 1 : span * 0.025;

    const paddedMin = chartMin < 0 ? chartMin - padding : chartMin;
    const paddedMax = chartMax > 0 ? chartMax + padding : chartMax;

    return {
        min: paddedMin,
        max: paddedMax,
    };
}

interface UseChartSync {
    hoveredTimestamp: Readonly<Ref<number | null>>;
    setHoverTimestamp(timestamp: number | null): void;

    viewRange: Readonly<Ref<IDateRange | null>>;
    setViewRange(timestamp: IDateRange | null): void;
}

interface UseYAxisSync {
    sharedYAxisRange: Readonly<Ref<{ min: number; max: number } | null>>;
    setSharedYAxisRange(range: { min: number; max: number } | null): void;
    updateYAxisRange(min: number, max: number): void;
}

const DEFAULT_Y_AXIS_SYNC_GROUP = '__default__';

const hoveredTimestamp = ref<number | null>(null);
const viewRange = ref<IDateRange | null>(null);
const yAxisRangeByGroup = new Map<string, Ref<{ min: number; max: number } | null>>();
const sharedSeriesVisibility = ref<Record<string, boolean>>({});

function getOrCreateYAxisRangeRef (groupKey: string): Ref<{ min: number; max: number } | null> {
    let rangeRef = yAxisRangeByGroup.get(groupKey);
    if (!rangeRef) {
        rangeRef = ref<{ min: number; max: number } | null>(null);
        yAxisRangeByGroup.set(groupKey, rangeRef);
    }
    return rangeRef;
}

/** Clears all keyed (non-default) Y-axis sync buckets; dashboard/modules keep using `useYAxisSync()` default bucket. */
export function clearNonDefaultYAxisSyncGroups (): void {
    yAxisRangeByGroup.forEach((rangeRef, key) => {
        if (key !== DEFAULT_Y_AXIS_SYNC_GROUP) {
            rangeRef.value = null;
        }
    });
}

export function useChartSync () : UseChartSync {
    function setHoverTimestamp (timestamp: number | null): void {
        hoveredTimestamp.value = timestamp;
    }

    function setViewRange (timestamp: IDateRange | null): void {
        viewRange.value = timestamp;
    }

    return {
        hoveredTimestamp: readonly(hoveredTimestamp),
        setHoverTimestamp,

        viewRange: readonly(viewRange),
        setViewRange,
    };
}

export function useYAxisSync (
    groupKey?: MaybeRefOrGetter<string | number | undefined>,
): UseYAxisSync {
    const resolvedGroupKey = computed(() => {
        if (groupKey === undefined) {
            return DEFAULT_Y_AXIS_SYNC_GROUP;
        }
        const raw = toValue(groupKey);
        if (raw === undefined || raw === null || raw === '') {
            return DEFAULT_Y_AXIS_SYNC_GROUP;
        }
        return String(raw);
    });

    const sharedYAxisRange = computed({
        get (): { min: number; max: number } | null {
            return getOrCreateYAxisRangeRef(resolvedGroupKey.value).value;
        },
        set (range: { min: number; max: number } | null): void {
            getOrCreateYAxisRangeRef(resolvedGroupKey.value).value = range;
        },
    });

    function setSharedYAxisRange (range: { min: number; max: number } | null): void {
        getOrCreateYAxisRangeRef(resolvedGroupKey.value).value = range;
    }

    function updateYAxisRange (min: number, max: number): void {
        const rangeRef = getOrCreateYAxisRangeRef(resolvedGroupKey.value);
        if (!rangeRef.value) {
            rangeRef.value = { min, max };
        } else {
            rangeRef.value = {
                min: Math.min(rangeRef.value.min, min),
                max: Math.max(rangeRef.value.max, max),
            };
        }
    }

    return {
        sharedYAxisRange: readonly(sharedYAxisRange),
        setSharedYAxisRange,
        updateYAxisRange,
    };
}

export interface UseLegendSync {
    sharedSeriesVisibility: Readonly<Ref<Record<string, boolean>>>;
    setSeriesVisibility(seriesId: string, visible: boolean): void;
    clearSeriesVisibility(): void;
}

export function useLegendSync (): UseLegendSync {
    function setSeriesVisibility (seriesId: string, visible: boolean): void {
        sharedSeriesVisibility.value = {
            ...sharedSeriesVisibility.value,
            [seriesId]: visible,
        };
    }

    function clearSeriesVisibility (): void {
        sharedSeriesVisibility.value = {};
    }

    return {
        sharedSeriesVisibility: readonly(sharedSeriesVisibility),
        setSeriesVisibility,
        clearSeriesVisibility,
    };
}
